/* eslint-disable react-hooks/exhaustive-deps */
import FormDownload from './FormDownload';
import FormUpload from './FormUpload';
import LoadingButton from '@/components/LoadingButton';
import { ROLE_TYPES } from '@/constants/roles';
import { ButtonEnhance } from '@/module/common/componentUI/commonStyleComponents';
import {
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  DialogActions,
} from '@material-ui/core';
import { ClearRounded } from '@material-ui/icons';
import { capitalize, isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const useStyle = makeStyles({
  titleDialog: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 600,
  },

  dialogContent: {
    padding: '24px',

    '& .module-upload-item': {
      marginBottom: 16,
      borderBottom: '1px solid #E1E1E1',
    },

    '& .module-upload-item:last-child': {
      borderBottom: 'none',
    },

    borderBottom: '1px solid #E1E1E1',
  },

  closeBtn: {
    cursor: 'pointer',
    float: 'right',
    color: '#CACFD3',
  },
});

const ModalShowResult = ({
  typeModal,
  onClose,
  onSubmit,
  listModule,
  selectedItem,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);

  const [listFiles, setListFiles] = useState([]);
  const [fileData, setFileData] = useState({});
  const [note, setNote] = useState('');

  const [defaultDoctor, setDefaultDoctor] = useState({});
  const [moduleDoctors, setModuleDoctors] = useState({});
  const [byPassTests, setByPassTests] = useState([]);
  const initialValuesRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const isDoctor = userInfo.roleName === ROLE_TYPES.DOCTOR;
  const isDisabledByRadiologies =
    typeModal === 'upload' && selectedItem?.hasRadiologiesProcedure === false;

  const classes = useStyle();

  const hasChanges = () => {
    if (!initialValuesRef.current) return true;
    const {
      defaultDoctor: initDoctor,
      moduleDoctors: initModuleDoctors,
      note: initNote,
      byPassTests: initByPassTests,
    } = initialValuesRef.current;
    if (listFiles.length > 0) return true;
    if (defaultDoctor?.id !== initDoctor?.id) return true;
    if (!isEqual(moduleDoctors, initModuleDoctors)) return true;
    if (note !== initNote) return true;
    if (!isEqual(byPassTests, initByPassTests)) return true;
    return false;
  };

  const onSubmitForm = async () => {
    if (isDisabledByRadiologies) return;

    const isDirty = hasChanges();
    const changedByPassTests = initialValuesRef.current
      ? byPassTests.filter((item) => {
          const initItem = initialValuesRef.current.byPassTests.find(
            (t) => t.moduleId === item.moduleId
          );
          return (
            initItem && item.isReviewRequired !== initItem.isReviewRequired
          );
        })
      : byPassTests;
    const submitData =
      typeModal === 'upload' && !isDirty
        ? {
            listFiles: [],
            defaultDoctor: {},
            moduleDoctors: {},
            note,
            byPassTests: [],
          }
        : {
            listFiles,
            defaultDoctor,
            moduleDoctors,
            note,
            byPassTests: changedByPassTests,
          };

    try {
      setBtnLoading(true);
      onSubmit(submitData, typeModal, (isSuccess) => {
        if (isSuccess) onClose();
        setBtnLoading(false);
      });
    } catch (errs) {
      setBtnLoading(false);
    }
  };

  const handleUpload = (key, data) => {
    if (key === 'file') {
      const newListFiles = [...listFiles];

      newListFiles.push(data);
      setFileData(data);
      setListFiles(newListFiles);
    }
  };

  useEffect(() => {
    if (listModule.length > 0) {
      // Initialize default doctor from filter API response
      const initDefaultDoctor = {
        id: selectedItem?.assignedDoctorId || null,
        fullName: selectedItem?.assignedDoctorName || null,
      };

      // Initialize per-module doctors from each module's assignedDoctorId
      const initModuleDoctors = {};
      listModule.forEach((item) => {
        if (item.assignedDoctorId) {
          initModuleDoctors[item.moduleId] = {
            id: item.assignedDoctorId,
            fullName: item.assignedDoctorName || null,
          };
        }
      });

      const initByPassTests = listModule.map((item) => ({
        moduleId: item.moduleId,
        isReviewRequired: item.isReviewRequired || false,
      }));

      setDefaultDoctor(initDefaultDoctor);
      setModuleDoctors(initModuleDoctors);
      setByPassTests(initByPassTests);
      initialValuesRef.current = {
        defaultDoctor: initDefaultDoctor,
        moduleDoctors: initModuleDoctors,
        byPassTests: initByPassTests,
        note: selectedItem?.note || '',
      };
    }
  }, [listModule]);

  useEffect(() => {
    // Keep note value when user re-opens upload dialog.
    if (typeModal === 'upload') {
      setNote(selectedItem?.note || '');
    }
  }, [selectedItem, typeModal]);

  return (
    <>
      <DialogTitle>
        <div className={classes.titleDialog}>
          {`${capitalize(typeModal)} Result`}
          <ClearRounded className={classes.closeBtn} onClick={onClose} />
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        {typeModal === 'download' ? (
          <FormDownload
            dataModule={listModule.filter(
              (d) => d.reviewStatus === 'Completed'
            )}
            hasIRReport={selectedItem.hasIRReport}
          />
        ) : (
          <FormUpload
            dataModule={listModule}
            label="Update Result PDF"
            fileData={fileData.url}
            onChange={handleUpload}
            listFiles={listFiles}
            isDoctor={isDoctor}
            defaultDoctor={defaultDoctor}
            setDefaultDoctor={setDefaultDoctor}
            moduleDoctors={moduleDoctors}
            setModuleDoctors={setModuleDoctors}
            note={note}
            setNote={setNote}
            byPassTests={byPassTests}
            setByPassTests={setByPassTests}
            isDisabledByRadiologies={isDisabledByRadiologies}
          />
        )}
      </DialogContent>
      <DialogActions>
        <ButtonEnhance
          background="#E9E9E9"
          color="black"
          backgroundHover="#ccc"
          onClick={onClose}
        >
          Cancel
        </ButtonEnhance>

        <LoadingButton
          showLoader={btnLoading}
          onClick={onSubmitForm}
          disabled={btnLoading || isDisabledByRadiologies}
        >
          {capitalize(typeModal)}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default ModalShowResult;
